import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
  Spinner,
  Badge
} from 'reactstrap';
import axios from 'axios';

const EvidenceDetail = ({ evidence, onBack }) => {
  const [formData, setFormData] = useState({
    name: evidence.name || '',
    type: evidence.type || 'other',
    url: evidence.url || '',
    notes: evidence.notes || '',
    linkedControls: Array.isArray(evidence.linkedControls) ? evidence.linkedControls.join(', ') : '',
    linkedSections: Array.isArray(evidence.linkedSections) ? evidence.linkedSections.join(', ') : '',
    sensitivity: evidence.sensitivity || 'internal',
    cadenceDays: evidence.cadenceDays || 30
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const parseArrayInput = (value) => {
    if (!value) return [];
    return value.split(',').map(v => v.trim()).filter(v => v);
  };

  const handleEdit = () => {
    setEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      name: evidence.name || '',
      type: evidence.type || 'other',
      url: evidence.url || '',
      notes: evidence.notes || '',
      linkedControls: Array.isArray(evidence.linkedControls) ? evidence.linkedControls.join(', ') : '',
      linkedSections: Array.isArray(evidence.linkedSections) ? evidence.linkedSections.join(', ') : '',
      sensitivity: evidence.sensitivity || 'internal',
      cadenceDays: evidence.cadenceDays || 30
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Evidence name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        ...formData,
        linkedControls: parseArrayInput(formData.linkedControls),
        linkedSections: parseArrayInput(formData.linkedSections)
      };

      const response = await axios.put(
        `${baseURL}/ssp-studio/evidence/${evidence._id}`,
        payload,
        { withCredentials: true }
      );

      if (response.data.success) {
        setSuccess('Evidence updated successfully');
        setEditing(false);
        setTimeout(() => setSuccess(null), 3000);
        // Update local evidence object
        Object.assign(evidence, response.data.data);
      } else {
        setError(response.data.message || 'Failed to update evidence');
      }
    } catch (err) {
      console.error('Error updating evidence:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const getSensitivityColor = (sensitivity) => {
    const colors = {
      public: 'success',
      internal: 'info',
      cui: 'warning',
      sensitive: 'danger'
    };
    return colors[sensitivity] || 'secondary';
  };

  const getTypeColor = (type) => {
    const colors = {
      scan: 'info',
      policy: 'primary',
      procedure: 'success',
      screenshot: 'warning',
      config: 'secondary',
      ticket: 'danger',
      other: 'dark'
    };
    return colors[type] || 'secondary';
  };

  const daysSinceUpdate = Math.floor(
    (Date.now() - new Date(evidence.lastUpdated)) / (1000 * 60 * 60 * 24)
  );
  const isExpiringSoon = daysSinceUpdate >= (evidence.cadenceDays - 7);

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <Button color="secondary" outline size="sm" onClick={onBack}>
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to Library
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <i className="mdi mdi-file-document-outline me-2"></i>
              Evidence Details
            </div>
            <div>
              {!editing && (
                <Button color="primary" size="sm" onClick={handleEdit}>
                  <i className="mdi mdi-pencil me-1"></i>
                  Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Metadata Banner */}
          <div className="bg-light p-3 rounded mb-4">
            <Row>
              <Col md={3}>
                <small className="text-muted d-block">Type</small>
                <Badge color={getTypeColor(evidence.type)} className="mt-1">
                  {evidence.type}
                </Badge>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Sensitivity</small>
                <Badge color={getSensitivityColor(evidence.sensitivity)} className="mt-1">
                  {evidence.sensitivity}
                </Badge>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Last Updated</small>
                <div className="mt-1">
                  {new Date(evidence.lastUpdated).toLocaleDateString()}
                  <small className="text-muted d-block">{daysSinceUpdate} days ago</small>
                </div>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">Status</small>
                <div className="mt-1">
                  {isExpiringSoon ? (
                    <Badge color="warning">
                      <i className="mdi mdi-alert me-1"></i>
                      Expiring Soon
                    </Badge>
                  ) : (
                    <Badge color="success">Current</Badge>
                  )}
                </div>
              </Col>
            </Row>
          </div>

          <Form onSubmit={handleSave}>
            <FormGroup>
              <Label for="name">
                Evidence Name <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={!editing || saving}
                plaintext={!editing}
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="type">Type</Label>
                  <Input
                    type="select"
                    id="type"
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    disabled={!editing || saving}
                  >
                    <option value="scan">Scan Report</option>
                    <option value="policy">Policy Document</option>
                    <option value="procedure">Procedure Document</option>
                    <option value="screenshot">Screenshot</option>
                    <option value="config">Configuration File</option>
                    <option value="ticket">Support Ticket</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="sensitivity">Sensitivity Level</Label>
                  <Input
                    type="select"
                    id="sensitivity"
                    value={formData.sensitivity}
                    onChange={(e) => handleChange('sensitivity', e.target.value)}
                    disabled={!editing || saving}
                  >
                    <option value="public">Public</option>
                    <option value="internal">Internal Use Only</option>
                    <option value="cui">Controlled Unclassified Information (CUI)</option>
                    <option value="sensitive">Sensitive/Confidential</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="url">URL / File Path</Label>
              <Input
                type="text"
                id="url"
                value={formData.url}
                onChange={(e) => handleChange('url', e.target.value)}
                disabled={!editing || saving}
                plaintext={!editing}
              />
              {formData.url && !editing && (
                <div className="mt-2">
                  <a href={formData.url} target="_blank" rel="noopener noreferrer">
                    <i className="mdi mdi-open-in-new me-1"></i>
                    Open Link
                  </a>
                </div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="notes">Notes / Description</Label>
              <Input
                type="textarea"
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={4}
                disabled={!editing || saving}
                plaintext={!editing}
              />
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="linkedControls">Linked NIST Controls</Label>
                  <Input
                    type="text"
                    id="linkedControls"
                    value={formData.linkedControls}
                    onChange={(e) => handleChange('linkedControls', e.target.value)}
                    disabled={!editing || saving}
                    plaintext={!editing}
                  />
                  {!editing && evidence.linkedControls && evidence.linkedControls.length > 0 && (
                    <div className="mt-2">
                      {evidence.linkedControls.map((ctrl, idx) => (
                        <Badge key={idx} color="secondary" className="me-1">
                          {ctrl}
                        </Badge>
                      ))}
                    </div>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="linkedSections">Linked SSP Sections</Label>
                  <Input
                    type="text"
                    id="linkedSections"
                    value={formData.linkedSections}
                    onChange={(e) => handleChange('linkedSections', e.target.value)}
                    disabled={!editing || saving}
                    plaintext={!editing}
                  />
                  {!editing && evidence.linkedSections && evidence.linkedSections.length > 0 && (
                    <div className="mt-2">
                      {evidence.linkedSections.map((sec, idx) => (
                        <Badge key={idx} color="info" className="me-1">
                          {sec}
                        </Badge>
                      ))}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label for="cadenceDays">
                Refresh Cadence (days)
              </Label>
              <Input
                type="number"
                id="cadenceDays"
                value={formData.cadenceDays}
                onChange={(e) => handleChange('cadenceDays', parseInt(e.target.value))}
                min={1}
                max={365}
                disabled={!editing || saving}
                plaintext={!editing}
              />
            </FormGroup>

            {editing && (
              <>
                <hr className="my-4" />
                <div className="d-flex justify-content-end gap-2">
                  <Button color="secondary" outline onClick={handleCancel} disabled={saving}>
                    Cancel
                  </Button>
                  <Button color="success" type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-check me-1"></i>
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </Form>
        </CardBody>
      </Card>

      {/* Additional Info */}
      <Card className="mt-3">
        <CardHeader>
          <i className="mdi mdi-information-outline me-2"></i>
          Metadata
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <small className="text-muted d-block">Created</small>
                <strong>{new Date(evidence.createdAt).toLocaleString()}</strong>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <small className="text-muted d-block">Last Modified</small>
                <strong>{new Date(evidence.updatedAt).toLocaleString()}</strong>
              </div>
            </Col>
          </Row>
          {evidence.ssp_id && (
            <div>
              <small className="text-muted d-block">Associated SSP ID</small>
              <code>{evidence.ssp_id}</code>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default EvidenceDetail;
